import React, { useState } from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { projectInput, requestProposal } from './aiWriting';
import SoldeCreditsIA from './SoldeCreditsIA';

// Champs que l'IA peut proposer, dans l'ordre d'affichage.
const FIELDS = [
  { key: 'title', label: 'Titre' },
  { key: 'description', label: 'Description' },
  { key: 'businessModel', label: 'Modèle économique' },
  { key: 'targetMarket', label: 'Marché cible' },
  { key: 'competitiveAdvantage', label: 'Avantage concurrentiel' },
];

/**
 * Aide à la rédaction d'un projet (POST /api/ai/write, tâche bourse_projet).
 * L'IA propose ; le porteur relit puis utilise ou ignore la proposition. Rien n'est
 * enregistré tant qu'il n'a pas soumis le formulaire.
 */
const AiWritingAssistant = ({ formData, countryName, onApply }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [proposal, setProposal] = useState(null);

  const ready = ((formData.title || '') + (formData.description || '')).replace(/\s/g, '').length >= 20;

  const ask = async () => {
    setLoading(true);
    setError(null);
    setProposal(null);
    try {
      const result = await requestProposal('bourse_projet', projectInput(formData, countryName));
      setProposal(result.proposal);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const proposedFields = proposal ? FIELDS.filter(f => proposal[f.key]) : [];

  const apply = () => {
    const updates = {};
    proposedFields.forEach(f => { updates[f.key] = proposal[f.key]; });
    onApply(updates);
    setProposal(null);
  };

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={ask}
        disabled={loading || !ready}
        title={ready ? 'Proposer une présentation plus claire du projet' : 'Écrivez d\'abord un titre et quelques phrases de description'}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <SparklesIcon className="h-4 w-4" />
        {loading ? 'Rédaction en cours…' : 'Améliorer la présentation avec l\'IA'}
      </button>
      <SoldeCreditsIA tache="bourse_projet" />

      {error && (
        <p className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
      )}

      {proposal && (
        <div className="mt-3 border border-primary-200 bg-primary-50 rounded-lg p-4 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
            Proposition de l'IA — relisez-la avant de l'utiliser
          </p>
          {proposedFields.map(f => (
            <div key={f.key}>
              <p className="text-xs font-semibold text-gray-500 mb-0.5">{f.label}</p>
              <p className="text-sm text-gray-800 whitespace-pre-line">{proposal[f.key]}</p>
            </div>
          ))}
          {proposal.tips?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-0.5">Pour convaincre un investisseur, pensez à préciser :</p>
              <ul className="list-disc pl-5 text-sm text-gray-700 space-y-0.5">
                {proposal.tips.map(tip => <li key={tip}>{tip}</li>)}
              </ul>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              type="button"
              onClick={apply}
              className="px-4 py-2 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700"
            >
              Utiliser cette proposition
            </button>
            <button
              type="button"
              onClick={() => setProposal(null)}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50"
            >
              Ignorer
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Les champs proposés remplacent ceux du formulaire ; les autres restent inchangés. Rien n'est publié avant que vous enregistriez le projet.
          </p>
        </div>
      )}
    </div>
  );
};

export default AiWritingAssistant;
